'use client';

import { useToast } from '@common/hooks';
import { CustomError } from '@core/utils';
import {
  useIsMutating,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';

import { AuthMutation, AuthQueryOptions } from '@/api';

import {
  type CloverDay,
  mapAttendanceDaysToClover,
} from '../_utils/resolveCloverStrip';

export interface AttendanceStatus {
  cloverDays: CloverDay[];
  buttonState: 'CHECK' | 'RESTART' | 'DONE';
  alreadyCheckedToday: boolean;
  todayDayIndex: number;
}

//* 서버가 최종 판정자인 성인 인증 실패 코드. 클릭 전 triggerGate(adult)로 대부분 차단되지만
//* 서버 응답 실패 시에도 동일 메시지를 안내한다.
const ADULT_ONLY_RETURN_CODE = 30403;

//* 사용자가 이 페이지에서 직접 바꾸는 자기 상태라, 진입할 때마다 서버와 재동기화한다.
//* (재진입 시 서버 프리페치가 체크인 직전 상태를 fresh 타임스탬프로 내려 클라 최신 캐시를
//* 덮고 staleTime이 고정하는 stale-clobber를 self-heal한다.)
export const useAttendance = () =>
  useSuspenseQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/benefit-attendance'](),
    refetchOnMount: 'always',
    select: (response): AttendanceStatus => ({
      cloverDays: mapAttendanceDaysToClover(response.context?.days ?? []),
      buttonState: response.context?.buttonState ?? 'CHECK',
      alreadyCheckedToday: response.context?.alreadyCheckedTodayYN ?? false,
      todayDayIndex: response.context?.todayDayIndex ?? 0,
    }),
  });

const CHECK_IN_MUTATION_KEY = ['benefit-attendance-check-in'];

//* 자동(혜택홈)·수동(출석 페이지) 체크인이 같은 키를 공유한다 — 한쪽이 진행 중이면
//* 다른 쪽 버튼을 잠가 이중 POST를 막는다.
export const useIsCheckingInAttendance = () =>
  useIsMutating({ mutationKey: CHECK_IN_MUTATION_KEY }) > 0;

interface CheckInAttendanceOptions {
  /**
   * 자동 플로우 전용 — 사용자가 행동하지 않았으므로 실패 토스트를 띄우지 않고,
   * 성공은 출석 완료 시트가 알리므로 성공 토스트도 띄우지 않는다.
   */
  silent?: boolean;
}

export const useCheckInAttendance = ({
  silent,
}: CheckInAttendanceOptions = {}) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationKey: CHECK_IN_MUTATION_KEY,
    ...AuthMutation['post:/wello/v2/auth/benefit-attendance'](),
    onSuccess: (response) => {
      const grantedLuckyCount = response.context?.grantedLuckyCount ?? 0;
      if (!silent && grantedLuckyCount > 0) {
        toast({ message: `행운권 ${grantedLuckyCount}장이 지급됐어요` });
      }

      //* invalidate Promise를 반환해 refetch 완료까지 isPending을 유지한다 —
      //* 새 buttonState(DONE)가 도착하기 전 버튼이 "출석체크하기"로 되살아나는 창 제거.
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey:
            AuthQueryOptions['get:/wello/v2/auth/benefit-attendance']()
              .queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey:
            AuthQueryOptions['get:/wello/v2/auth/benefit-tickets']().queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: AuthQueryOptions['get:/wello/v2/auth/point']().queryKey,
        }),
      ]);
    },
    onError: (error) => {
      if (silent) {
        return;
      }

      if (
        error instanceof CustomError &&
        error.return_code === ADULT_ONLY_RETURN_CODE
      ) {
        toast({ message: '만 19세 이상만 참여할 수 있어요', type: 'fail' });

        return;
      }

      toast({
        message: '출석체크에 실패했어요. 다시 시도해 주세요.',
        type: 'fail',
      });
    },
  });
};
